import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  Button,
  Paper,
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  InputLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PropertyLossForm = ({ employee_id, financial_year }) => {
  const [employeeId, setEmployeeId] = useState(employee_id || '');
  const [fiscalYear, setFiscalYear] = useState(financial_year || '');
  const [claimingLoss, setClaimingLoss] = useState('Yes');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const [selfOccupiedAmount, setSelfOccupiedAmount] = useState('');
  const [letOutLossAmount, setLetOutLossAmount] = useState('');
  const [letOutIncomeAmount, setLetOutIncomeAmount] = useState('');

  const [isFirstResidential1, setIsFirstResidential1] = useState('Yes');
  const [interestAmount1, setInterestAmount1] = useState('');
  const [address1, setAddress1] = useState('');
  const [occupationDate1, setOccupationDate1] = useState(null);
  const [city1, setCity1] = useState('');
  const [loanSanctionDate1, setLoanSanctionDate1] = useState(null);
  const [houseValue1, setHouseValue1] = useState('');
  const [lenderName1, setLenderName1] = useState('');
  const [lenderAddress1, setLenderAddress1] = useState('');
  const [lenderPAN1, setLenderPAN1] = useState('');

  const [isFirstResidential2, setIsFirstResidential2] = useState('No');
  const [interestAmount2, setInterestAmount2] = useState('');
  const [address2, setAddress2] = useState('');
  const [occupationDate2, setOccupationDate2] = useState(null);
  const [city2, setCity2] = useState('');
  const [loanSanctionDate2, setLoanSanctionDate2] = useState(null);
  const [houseValue2, setHouseValue2] = useState('');
  const [lenderName2, setLenderName2] = useState('');
  const [lenderAddress2, setLenderAddress2] = useState('');
  const [lenderPAN2, setLenderPAN2] = useState('');

  const [letOutAddress, setLetOutAddress] = useState('');
  const [letOutOccupationDate, setLetOutOccupationDate] = useState(null);
  const [rentalIncome, setRentalIncome] = useState('');
  const [municipalTax, setMunicipalTax] = useState('');
  const [netAnnualValue, setNetAnnualValue] = useState(0);
  const [repairsValue, setRepairsValue] = useState(0);
  const [netRentalIncome, setNetRentalIncome] = useState(0);
  const [interestOnLoan, setInterestOnLoan] = useState('');
  const [totalInterestPaid, setTotalInterestPaid] = useState('');
  const [letOutLenderName, setLetOutLenderName] = useState('');
  const [carryForwardLoss, setCarryForwardLoss] = useState('');
  const [intraHeadSetOff, setIntraHeadSetOff] = useState('');
  const [letOutLenderAddress, setLetOutLenderAddress] = useState('');
  const [letOutLenderPAN, setLetOutLenderPAN] = useState('');

  const cities = [
    'Bangalore', 'Mumbai', 'Delhi', 'Chennai', 'Hyderabad',
    'Kolkata', 'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow'
  ];

  // Updated validation functions
  const validateAmount = (value, maxLimit = 200000) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = Number(value);
    if (isNaN(num) || num < 0) return '';
    if (num > maxLimit) return maxLimit.toString();
    return value.toString();
  };

  const validateAmountNotNegative = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = Number(value);
    if (isNaN(num) || num < 0) return '';
    return value.toString();
  };

  const isValidPAN = (pan) => {
    // Indian PAN format: 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return panRegex.test(pan.toUpperCase());
  };

  const handlePANChange = (value, setter) => {
    // Convert to uppercase and limit to 10 characters
    const upperValue = value.toUpperCase().slice(0, 10);
    setter(upperValue);
  };

  // Calculate net values when rental income or municipal tax changes
  useEffect(() => {
    const rental = Number(rentalIncome) || 0;
    const municipal = Number(municipalTax) || 0;

    const netValue = rental - municipal;
    setNetAnnualValue(netValue);

    const repairs = netValue * 0.3;
    setRepairsValue(repairs);

    const netRental = netValue - repairs;
    setNetRentalIncome(netRental);
  }, [rentalIncome, municipalTax]);

  useEffect(() => {

    if (employee_id && financial_year) {
      setEmployeeId(employee_id);
      setFiscalYear(financial_year);
      fetchPropertyData();
    }
  }, [employee_id, financial_year]);

  const fetchPropertyData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/property-loss/${employeeId}/${fiscalYear}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,

          }
        }
      );
      if (response.data) {
        const data = response.data;
        setClaimingLoss(data.claimingLoss || 'Yes');
        setSelfOccupiedAmount(data.selfOccupiedAmount?.toString() || '');
        setLetOutLossAmount(data.letOutLossAmount?.toString() || '');
        setLetOutIncomeAmount(data.letOutIncomeAmount?.toString() || '');
      }
    } catch (error) {
      //console.error('Error fetching property data:', error);
      setNotification({
        open: true,
        message: 'Failed to load property data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = {
      employeeId,
      fiscalYear,
      claimingLoss,
      selfOccupiedProperty: {
        amount: Number(selfOccupiedAmount) || 0,
      },
      letOutProperty: {
        lossAmount: Number(letOutLossAmount) || 0,
        incomeAmount: Number(letOutIncomeAmount) || 0,
      },
      selfOccupiedProperty1: {
        isFirstResidential: isFirstResidential1,
        interestAmount: Number(interestAmount1) || 0,
        address: address1,
        occupationDate: occupationDate1,
        city: city1,
        loanSanctionDate: loanSanctionDate1,
        houseValue: Number(houseValue1) || 0,
        lenderName: lenderName1,
        lenderAddress: lenderAddress1,
        lenderPAN: lenderPAN1,
      },
      selfOccupiedProperty2: {
        isFirstResidential: isFirstResidential2,
        interestAmount: Number(interestAmount2) || 0,
        address: address2,
        occupationDate: occupationDate2,
        city: city2,
        loanSanctionDate: loanSanctionDate2,
        houseValue: Number(houseValue2) || 0,
        lenderName: lenderName2,
        lenderAddress: lenderAddress2,
        lenderPAN: lenderPAN2,
      },
      letOutPropertyDetails: {
        address: letOutAddress,
        occupationDate: letOutOccupationDate,
        rentalIncome: Number(rentalIncome) || 0,
        municipalTax: Number(municipalTax) || 0,
        netAnnualValue,
        repairsValue,
        netRentalIncome,
        interestOnLoan: Number(interestOnLoan) || 0,
        totalInterestPaid: Number(totalInterestPaid) || 0,
        lenderName: letOutLenderName,
        carryForwardLoss: Number(carryForwardLoss) || 0,
        intraHeadSetOff: Number(intraHeadSetOff) || 0,
        lenderAddress: letOutLenderAddress,
        lenderPAN: letOutLenderPAN,
      }
    };

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      await axios.post(`${API_URL}/api/property-loss`, formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      setNotification({
        open: true,
        message: 'Property loss data saved successfully',
        severity: 'success'
      });
    } catch (error) {
      //console.error('Error saving property data:', error);
      setNotification({
        open: true,
        message: 'Failed to save property data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom justifyContent="center" align="center" >
            Loss on House Property
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2 }}>
            <Grid container spacing={3}>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="employeeId"
                  label="Employee ID"
                  value={employeeId}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="fiscalYear"
                  label="Financial Year"
                  value={fiscalYear}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Loss On House Property:</FormLabel>
                  <RadioGroup
                    row
                    value={claimingLoss}
                    onChange={(e) => setClaimingLoss(e.target.value)}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {claimingLoss === 'Yes' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Particulars
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={2}>

                        <Grid item xs={8}>
                          <Typography variant="subtitle1">Self-Occupied Property</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            fullWidth
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={selfOccupiedAmount}
                            onChange={(e) => setSelfOccupiedAmount(validateAmount(e.target.value))}
                            helperText="Limit: ₹2,00,000"
                          />
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="subtitle1">Loss from Let Out Property</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            fullWidth
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={letOutLossAmount}
                            onChange={(e) => setLetOutLossAmount(validateAmount(e.target.value))}
                            helperText="Limit: ₹2,00,000"
                          />
                        </Grid>

                        <Grid item xs={8}>
                          <Typography variant="subtitle1">Income from Let Out Property</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            fullWidth
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={letOutIncomeAmount}
                            onChange={(e) => setLetOutIncomeAmount(validateAmount(e.target.value))}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Self-Occupied Property 1
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend">First Residential Property:</FormLabel>
                            <RadioGroup
                              row
                              value={isFirstResidential1}
                              onChange={(e) => setIsFirstResidential1(e.target.value)}
                            >
                              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                              <FormControlLabel value="No" control={<Radio />} label="No" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Interest Amount"
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={interestAmount1}
                            onChange={(e) => setInterestAmount1(validateAmount(e.target.value))}
                            helperText="Limit: ₹2,00,000"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Actual Value of the House Property"
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={houseValue1}
                            onChange={(e) => setHouseValue1(validateAmountNotNegative(e.target.value))}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address of Property"
                            value={address1}
                            onChange={(e) => setAddress1(e.target.value)}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <DatePicker
                              label="Date of Occupation"
                              value={occupationDate1}
                              onChange={(date) => setOccupationDate1(date)}
                              renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <DatePicker
                              label="Loan Sanction Date"
                              value={loanSanctionDate1}
                              onChange={(date) => setLoanSanctionDate1(date)}
                              renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="city-label">City</InputLabel>
                            <Select
                              labelId="city-label"
                              value={city1}
                              label="City"
                              onChange={(e) => setCity1(e.target.value)}
                            >
                              {cities.map((city) => (
                                <MenuItem key={city} value={city}>{city}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Name of the Lender"
                            value={lenderName1}
                            onChange={(e) => setLenderName1(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="PAN of the Lender"
                            value={lenderPAN1}
                            onChange={(e) => handlePANChange(e.target.value, setLenderPAN1)}
                            error={lenderPAN1 !== '' && !isValidPAN(lenderPAN1)}
                            helperText={lenderPAN1 !== '' && !isValidPAN(lenderPAN1) ? "Invalid PAN format (e.g., ABCDE1234F)" : "10 characters: 5 letters, 4 digits, 1 letter"}
                            inputProps={{ maxLength: 10 }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Address of the Lender"
                            value={lenderAddress1}
                            onChange={(e) => setLenderAddress1(e.target.value)}
                          />
                        </Grid>

                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Self-Occupied Property 2
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend">First Residential Property:</FormLabel>
                            <RadioGroup
                              row
                              value={isFirstResidential2}
                              onChange={(e) => setIsFirstResidential2(e.target.value)}
                            >
                              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                              <FormControlLabel value="No" control={<Radio />} label="No" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Interest Amount"
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={interestAmount2}
                            onChange={(e) => setInterestAmount2(validateAmount(e.target.value))}
                            helperText="Limit: ₹2,00,000"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Actual Value of the House Property"
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={houseValue2}
                            onChange={(e) => setHouseValue2(validateAmountNotNegative(e.target.value))}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address of Property"
                            value={address2}
                            onChange={(e) => setAddress2(e.target.value)}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <DatePicker
                              label="Date of Occupation"
                              value={occupationDate2}
                              onChange={(date) => setOccupationDate2(date)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                />
                              )}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <DatePicker
                              label="Loan Sanction Date"
                              value={loanSanctionDate2}
                              onChange={(date) => setLoanSanctionDate2(date)}
                              renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel id="city2-label">City</InputLabel>
                            <Select
                              labelId="city2-label"
                              value={city2}
                              label="City"
                              onChange={(e) => setCity2(e.target.value)}
                            >
                              {cities.map((city) => (
                                <MenuItem key={city} value={city}>{city}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Name of the Lender"
                            value={lenderName2}
                            onChange={(e) => setLenderName2(e.target.value)}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="PAN of the Lender"
                            value={lenderPAN2}
                            onChange={(e) => handlePANChange(e.target.value, setLenderPAN2)}
                            error={lenderPAN2 !== '' && !isValidPAN(lenderPAN2)}
                            helperText={lenderPAN2 !== '' && !isValidPAN(lenderPAN2) ? "Invalid PAN format (e.g., ABCDE1234F)" : "10 characters: 5 letters, 4 digits, 1 letter"}
                            inputProps={{ maxLength: 10 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Address of the Lender"
                            value={lenderAddress2}
                            onChange={(e) => setLenderAddress2(e.target.value)}
                          />
                        </Grid>

                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Let-Out Property Details
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address of Property"
                            value={letOutAddress}
                            onChange={(e) => setLetOutAddress(e.target.value)}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <DatePicker
                              fullWidth
                              label="Date of Occupation (DD/MM/YYYY)"
                              value={letOutOccupationDate}
                              onChange={(date) => setLetOutOccupationDate(date)}
                              renderInput={(params) => <TextField {...params} fullWidth />}
                              placeholder="01/04/2025"
                            />
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Rental Income Received/Receivable for the Financial Year"
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={rentalIncome}
                            onChange={(e) => setRentalIncome(validateAmountNotNegative(e.target.value))}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Less: Municipal Tax Paid"
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={municipalTax}
                            onChange={(e) => setMunicipalTax(validateAmountNotNegative(e.target.value))}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Net Annual Value"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                              readOnly: true,
                            }}
                            value={netAnnualValue}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Less: Repairs (30% of Net Annual Value)"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                              readOnly: true,
                            }}
                            value={repairsValue.toFixed(2)}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Net Rental Income"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                              readOnly: true,
                            }}
                            value={netRentalIncome.toFixed(2)}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Less: Interest (Paid or Payable) on Borrowed Capital"
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={interestOnLoan}
                            onChange={(e) => setInterestOnLoan(validateAmountNotNegative(e.target.value))}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Total Interest Paid by Me (post-construction/possession)"
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={totalInterestPaid}
                            onChange={(e) => setTotalInterestPaid(validateAmountNotNegative(e.target.value))}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Name of the Lender"
                            value={letOutLenderName}
                            onChange={(e) => setLetOutLenderName(e.target.value)}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Carry Forward Loss Amount from Previous Financial Years"
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={carryForwardLoss}
                            onChange={(e) => setCarryForwardLoss(validateAmountNotNegative(e.target.value))}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Address of the Lender"
                            value={letOutLenderAddress}
                            onChange={(e) => setLetOutLenderAddress(e.target.value)}
                            placeholder="SBI Sanjaynagar, Bangalore"
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="PAN of the Lender"
                            value={letOutLenderPAN}
                            onChange={(e) => handlePANChange(e.target.value, setLetOutLenderPAN)}
                            error={letOutLenderPAN !== '' && !isValidPAN(letOutLenderPAN)}
                            helperText={letOutLenderPAN !== '' && !isValidPAN(letOutLenderPAN) ? "Invalid PAN format (e.g., ABCDE1234F)" : "10 characters: 5 letters, 4 digits, 1 letter"}
                            inputProps={{ maxLength: 10 }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Intra-head Set Off (Loss with Income from Let-out Property)"
                            type="number"
                            placeholder="0"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            value={intraHeadSetOff}
                            onChange={(e) => setIntraHeadSetOff(validateAmountNotNegative(e.target.value))}
                          />
                        </Grid>

                      </Grid>
                    </Paper>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="center" gap={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading}
                    sx={{ minWidth: 120 }}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                  {/* <Button
                    variant="outlined"
                    color="secondary"
                    size="large"
                    onClick={() => {
                      // Reset form logic can be added here
                      setClaimingLoss('Yes');
                      setSelfOccupiedAmount('');
                      setLetOutLossAmount('');
                      setLetOutIncomeAmount('');
                      // Reset all other fields as needed
                    }}
                    sx={{ minWidth: 120 }}
                  >
                    Reset
                  </Button> */}
                </Box>
              </Grid>

            </Grid>
          </Box>
        </Paper>

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>

      </Container>
    </LocalizationProvider>
  );
};

export default PropertyLossForm;